export type VideoSource =
  | { kind: 'youtube'; videoId: string; watchUrl: string }
  | { kind: 'embed'; provider: 'Vimeo'; embedUrl: string; watchUrl: string }
  | { kind: 'file'; src: string }
  | { kind: 'link'; watchUrl: string }

const youtubeId = /^[a-zA-Z0-9_-]{11}$/
const vimeoId = /^\d+$/
const videoFile = /\.(mp4|webm|ogv)$/i

const youtubeVideoId = (url: URL, host: string): string | null => {
  const pathParts = url.pathname.split('/').filter(Boolean)
  if (host === 'youtu.be') return pathParts[0] || null
  if (url.pathname === '/watch') return url.searchParams.get('v')
  return ['embed', 'shorts', 'live', 'v'].includes(pathParts[0]) ? pathParts[1] || null : null
}

export const resolveVideoSource = (input?: string): VideoSource | null => {
  const value = input?.trim()
  if (!value) return null

  const isLocalPath = value.startsWith('/') && !value.startsWith('//')
  if (!isLocalPath && !value.startsWith('https://')) return null

  let url: URL
  try {
    url = new URL(value, 'https://urusadvisory.com')
  } catch {
    return null
  }

  if (url.protocol !== 'https:') return null

  const resolvedUrl = isLocalPath ? `${url.pathname}${url.search}${url.hash}` : url.toString()
  if (videoFile.test(url.pathname)) return { kind: 'file', src: resolvedUrl }
  if (isLocalPath) return null

  const host = url.hostname.replace(/^(www|m)\./, '').toLowerCase()
  if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'youtu.be') {
    const id = youtubeVideoId(url, host)
    if (!id) return { kind: 'link', watchUrl: resolvedUrl }
    if (!youtubeId.test(id)) return null
    return { kind: 'youtube', videoId: id, watchUrl: `https://www.youtube.com/watch?v=${id}` }
  }

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean).at(-1)
    if (!id || !vimeoId.test(id)) return { kind: 'link', watchUrl: resolvedUrl }
    return {
      kind: 'embed',
      provider: 'Vimeo',
      embedUrl: `https://player.vimeo.com/video/${id}?autoplay=1&controls=1`,
      watchUrl: `https://vimeo.com/${id}`,
    }
  }

  return { kind: 'link', watchUrl: resolvedUrl }
}
